import { useQuery } from '@apollo/client';
import { useAddProduct } from '@hooks/useAddProduct';
import { useModalRouter } from '@hooks/useModalRouter';
import { useOrderState } from '@hooks/useOrderState';
import { Button, CircularProgress, MenuProps } from '@mui/material';
import { styled } from '@mui/system';
import {
  getProduct,
  getProductQueryParams,
  getProductQueryResponse,
} from '@queries/product/queries';
import { useUpdateQuantity } from '../hooks/useUpdateQuantity';
import { useCartMenu } from '@hooks/useCartMenu';
import { useState, useCallback, useEffect } from 'react';
import { usePermissions } from '@hooks/usePermissions';
import { LetMeKnownWhenItsAvailableModal } from './LetMeKnowWhenItsAvailableModal';
import { Product } from '@interfaces';
import { useSegment } from '@hooks/useSegment';
import CloseIcon from '@mui/icons-material/Close';
import { useWindowSize } from '@hooks/useWindowSize';
import { pluralize } from '../utils/pluralize';
import {
  Container,
  AddContainer,
  AddButton,
  UnitsContainer,
  HeaderModal,
  SelectContainer,
  MenuItemText,
  QuantityText,
  HelperQuantityText,
  StyledCircularProgress,
} from './ActionContainer.styles';
import { useScroll } from '@hooks/useScroll';
import { ProductInteractionLocation } from '@validators/Events/Product';
import { ProductQuantityActions } from '@components/Product/ProductQuantityActions';

interface GetStockMessageProps {
  prescriptionType: string;
  quantity: number;
  maxAmount: number;
  isMaxAmount: boolean;
}
const getStockMessage = ({
  prescriptionType,
  quantity,
  maxAmount,
  isMaxAmount,
}: GetStockMessageProps) => {
  if (prescriptionType === 'HELD') {
    if (!quantity) return '';
    return `${quantity} ${pluralize('unidad', quantity, 'es')} en el carro`;
  }
  if (maxAmount > 100) {
    return '';
  }
  if (quantity === 0) {
    return maxAmount !== 6
      ? `${maxAmount} ${pluralize('unidad', maxAmount, 'es')} en stock`
      : `Máximo ${maxAmount} ${pluralize('unidad', maxAmount, 'es')}`;
  }

  if (isMaxAmount) {
    return 'Has agregado el máximo de unidades posibles al carro';
  }

  return maxAmount !== 6
    ? `${quantity} ${pluralize(
        'unidad',
        quantity,
        'es',
      )} en el carro de ${maxAmount} en stock`
    : `${quantity} ${pluralize(
        'unidad',
        quantity,
        'es',
      )} en el carro, máximo ${maxAmount}`;
};

interface ActionsProps {
  product: Product;
  isSelectOpen: boolean;
  handleSelectFocus: () => void;
  handleSelectBlur: () => void;
}

interface CustomMenuProps {
  isMobile: boolean;
  height: number;
}

const CustomMenu = ({
  isMobile,
  height,
}: CustomMenuProps): Partial<MenuProps> => ({
  anchorReference: isMobile ? 'anchorPosition' : 'anchorEl',
  anchorPosition: isMobile ? { top: height, left: 0 } : undefined,
  transformOrigin: isMobile
    ? {
        vertical: 'bottom',
        horizontal: 'center',
      }
    : {
        vertical: 'top',
        horizontal: 'center',
      },
  PaperProps: {
    sx: isMobile
      ? {
          width: '100%',
          bottom: 0,
          borderRadius: '12px 12px 0 0',
          backgroundColor: 'white',
          position: 'fixed',
          maxWidth: 'unset',
          left: '0 !important',
          top: 'auto !important',
        }
      : { maxHeight: 300 },
  },
  disableScrollLock: true,
  disablePortal: true,
});

const LoadingButton = styled(CircularProgress)`
  color: white;
`;

export const ActionContainer = ({
  product,
  isSelectOpen,
  handleSelectFocus,
  handleSelectBlur,
}: ActionsProps) => {
  const { isMobile, height } = useWindowSize();
  const modalRouter = useModalRouter();
  const { track } = useSegment();
  const { prodSlug } = modalRouter.query;

  const { userCan } = usePermissions();
  const isAssistant = userCan('MANAGE_USER');
  const isLocalVendor = userCan('LOCAL_VENDOR');

  const { order } = useOrderState();
  const cartMenu = useCartMenu();
  const scroll = useScroll();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);

  const { data: { product: freshProduct } = {} } = useQuery<
    getProductQueryResponse,
    getProductQueryParams
  >(getProduct('getFreshProductShow'), {
    variables: { params: { slug: prodSlug as string } },
    returnPartialData: true,
    fetchPolicy: 'cache-only',
  });

  const { handleAddProduct, loading: addingProduct } = useAddProduct({
    location: ProductInteractionLocation.ProductView,
  });
  const { quantity, addQuantity, subQuantity, MAX_AMOUNT } = useUpdateQuantity({
    product: freshProduct,
    location: ProductInteractionLocation.ProductView,
  });

  const handleOpenCart = () => {
    cartMenu.open();
    setLoading(false);
  };

  const inCart = order?.orderDetails.find(
    (detail) => detail.product.id === freshProduct?.id,
  );
  const handleAddButton = async () => {
    setLoading(true);
    if (isLocalVendor && product?.availability?.status !== 'AVAILABLE_24_HRS')
      return;
    if (quantity + selectedQuantity > MAX_AMOUNT) {
      handleOpenCart();
      return;
    }
    if (inCart) {
      addQuantity(selectedQuantity);
      setTimeout(() => {
        cartMenu.open();
        setLoading(false);
      }, 1000);

      return;
    }
    await handleAddProduct({
      puuid: freshProduct?.uuid,
      product: freshProduct,
      quantity: selectedQuantity,
    });
    cartMenu.open();
    setLoading(false);
  };

  const handleCloseModal = useCallback(() => {
    setOpenDialog(false);
  }, []);

  useEffect(() => {
    if (quantity + selectedQuantity > MAX_AMOUNT || !selectedQuantity) {
      setSelectedQuantity(1);
    }
  }, [MAX_AMOUNT, quantity, selectedQuantity]);

  useEffect(() => {
    setSelectedQuantity(1);
  }, [product]);
  useEffect(
    function disableScrollOnQuantitySelectOpenChange() {
      const tag = 'product-page-action-container';
      if (isSelectOpen) {
        scroll.disableScroll(tag);
      } else {
        scroll.allowScroll(tag);
      }
    },
    [isSelectOpen],
  );

  const isMaxAmount = quantity >= MAX_AMOUNT;
  const helperText = getStockMessage({
    prescriptionType: freshProduct?.prescriptionType,
    quantity,
    maxAmount: MAX_AMOUNT,
    isMaxAmount,
  });
  const isRemovingProduct = quantity === 0 && !!inCart;

  if (
    !isAssistant &&
    freshProduct?.prescriptionType === 'CHECK' &&
    !isLocalVendor
  )
    return null;

  if (
    ['SOLD_OUT', 'UNAVAILABLE', 'HIDDEN'].includes(
      freshProduct?.availability?.status,
    )
  ) {
    return (
      <>
        <Container>
          <AddContainer>
            <AddButton
              variant="contained"
              onClick={() => {
                void track('let me know when available', {
                  availability: product.availability?.status,
                  brand: product.laboratory,
                  id: product.id,
                  name: product.name,
                });
                setOpenDialog(true);
              }}
            >
              Avísame cuando esté disponible
            </AddButton>
          </AddContainer>
        </Container>
        {openDialog && (
          <LetMeKnownWhenItsAvailableModal
            openDialog={openDialog}
            setClose={handleCloseModal}
            product={product}
          />
        )}
      </>
    );
  }

  const optionsLength = Number.isInteger(MAX_AMOUNT - quantity)
    ? Math.min(20, MAX_AMOUNT - quantity)
    : 0;

  if (
    isLocalVendor &&
    freshProduct.availability?.status !== 'AVAILABLE_24_HRS'
  ) {
    return null;
  }

  return (
    <Container>
      {isRemovingProduct ? (
        <StyledCircularProgress>
          <CircularProgress size={26} />
        </StyledCircularProgress>
      ) : inCart ? (
        <ProductQuantityActions
          quantity={quantity}
          MAX_AMOUNT={MAX_AMOUNT}
          addQuantity={addQuantity}
          subQuantity={subQuantity}
          variant="productView"
        />
      ) : (
        <>
          <UnitsContainer>
            <SelectContainer
              value={selectedQuantity}
              onChange={(event) =>
                setSelectedQuantity(Number(event.target.value))
              }
              displayEmpty
              open={isSelectOpen}
              onOpen={handleSelectFocus}
              onClose={handleSelectBlur}
              disabled={addingProduct || (inCart && isMaxAmount)}
              renderValue={(selected) => {
                return (
                  <>
                    Cantidad: <QuantityText>{selected}</QuantityText>{' '}
                    {helperText && (
                      <HelperQuantityText>({helperText})</HelperQuantityText>
                    )}
                  </>
                );
              }}
              MenuProps={CustomMenu({ isMobile, height })}
            >
              {isMobile && (
                <HeaderModal>
                  Elige cantidad
                  <Button onClick={handleSelectBlur} sx={{ padding: 0 }}>
                    <CloseIcon />
                  </Button>
                </HeaderModal>
              )}
              {optionsLength &&
                Array.from({ length: optionsLength }, (_, i) => (
                  <MenuItemText key={i + 1} value={i + 1}>
                    {i + 1} {pluralize('unidad', i + 1, 'es')}
                  </MenuItemText>
                ))}
            </SelectContainer>
          </UnitsContainer>

          <AddContainer>
            <AddButton variant="contained" onClick={handleAddButton}>
              {loading ? (
                <LoadingButton size={24} />
              ) : isMaxAmount ? (
                'Ir al carro'
              ) : (
                'Agregar al carro'
              )}
            </AddButton>
          </AddContainer>
        </>
      )}
    </Container>
  );
};
